import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import { companyPath, formatInstant, whoName } from "./lib/format";
import { formatPct, formatPoints, formatShares, formatUsd } from "./lib/money";
import {
  fillStateLabel,
  orderSize,
  orderStatusLabel,
  planStatusLabel,
  sideLabel,
  skipReasonLabel,
} from "./lib/target";

const LIVE = new Set(["open", "executing"]);

/** One fill of an order, as recorded: its state, shares, price, dollars and when. */
function FillLine({ fill }) {
  const shares = formatShares(fill.quantity);
  const parts = [fillStateLabel(fill.state)];
  if (shares && shares !== "0") {
    parts.push(
      `${shares} ${shares === "1" ? "share" : "shares"} at ${formatUsd(fill.average_price) ?? "an unknown price"}` +
        (formatUsd(fill.amount) ? ` (${formatUsd(fill.amount)})` : ""),
    );
  } else {
    parts.push("nothing filled yet");
  }
  const when = formatInstant(fill.filled_at);
  if (when) parts.push(when);
  return (
    <li className="text-sm wrap-anywhere">
      {parts.join(" · ")}
      <span className="text-muted-foreground"> — broker order {plain(fill.broker_order_id)}</span>
    </li>
  );
}

FillLine.propTypes = {
  fill: PropTypes.shape({
    state: PropTypes.string.isRequired,
    quantity: PropTypes.string,
    average_price: PropTypes.string,
    amount: PropTypes.string,
    filled_at: PropTypes.string,
    broker_order_id: PropTypes.string.isRequired,
  }).isRequired,
};

/** One order of the plan: what to place, why, what became of it, and its fills. */
function OrderRow({ order, fills }) {
  return (
    <li data-order={order.id} className="border-b border-border py-2 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <Badge variant={order.side === "sell" ? "secondary" : "outline"}>{sideLabel(order.side)}</Badge>{" "}
        <Link
          to={companyPath(order.symbol)}
          className="inline-flex min-h-11 min-w-11 items-center font-semibold underline-offset-2 hover:underline md:min-h-0 md:min-w-0"
        >
          {order.symbol}
        </Link>{" "}
        <span className="text-sm">{orderSize(order)}</span>
      </div>
      <p className="m-0 text-sm font-medium">{orderStatusLabel(order.status)}</p>
      {order.after_sells && (
        <p className="m-0 text-sm">
          Waits for the sells: placed once this plan&apos;s sells have filled — the cash on hand can&apos;t cover it.
        </p>
      )}
      <p className="m-0 text-xs text-muted-foreground">
        Target {formatPct(order.target_weight)} · held {formatPct(order.actual_weight)} · drift{" "}
        {formatPoints(order.drift)}
      </p>
      {order.review_notes && (
        <p className="m-0 text-sm wrap-anywhere">Review: {plain(order.review_notes)}</p>
      )}
      <p className="m-0 text-xs text-muted-foreground wrap-anywhere">ref_id {order.ref_id}</p>
      {fills.length > 0 ? (
        <ul aria-label={`Fills of ${order.symbol} order ${order.id}`} className="mt-1 mb-0 flex flex-col gap-0.5 ps-4">
          {fills.map((fill) => (
            <FillLine key={fill.id} fill={fill} />
          ))}
        </ul>
      ) : (
        <p className="m-0 text-xs text-muted-foreground">No fill recorded yet.</p>
      )}
    </li>
  );
}

OrderRow.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number.isRequired,
    symbol: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    after_sells: PropTypes.bool,
    target_weight: PropTypes.string,
    actual_weight: PropTypes.string,
    drift: PropTypes.string,
    review_notes: PropTypes.string,
    ref_id: PropTypes.string,
  }).isRequired,
  fills: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/**
 * The latest sync plan (spec §5.4, walkthrough 9): the orders the server
 * computed to move the account from a snapshot to the target — side,
 * symbol, size, limit, whether a buy waits for the sells, status, the
 * executor's review notes — and each one's fills; what it skipped and why.
 * Reloads when `reloadTick` moves; only the newest request lands.
 */
function SyncPlanPanel({ reloadTick }) {
  const [state, setState] = useState({ loading: true, error: null, plan: null, total: 0, fills: [], fillsTotal: 0 });
  const [retryTick, setRetryTick] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    const isLatest = () => request === requestRef.current;
    setState((now) => ({ ...now, loading: true, error: null }));
    (async () => {
      try {
        const { data: page } = await investingApi.plans.list();
        if (!isLatest()) return;
        const newest = page?.results?.[0];
        if (!newest) {
          setState({ loading: false, error: null, plan: null, total: 0, fills: [], fillsTotal: 0 });
          return;
        }
        const [{ data: plan }, { data: fills }] = await Promise.all([
          investingApi.plans.get(newest.id),
          investingApi.fills.list({ plan: newest.id }),
        ]);
        if (!isLatest()) return;
        setState({
          loading: false,
          error: null,
          plan,
          total: page.count ?? 1,
          fills: fills?.results || [],
          fillsTotal: fills?.count ?? 0,
        });
      } catch (err) {
        if (!isLatest()) return;
        setState((now) => ({ ...now, loading: false, error: err }));
      }
    })();
  }, [reloadTick, retryTick]);

  const { loading, error, plan, total, fills, fillsTotal } = state;
  let body;
  if (error) {
    body = (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>{apiErrorMessage(error, "Couldn't load the sync plan.")}</span>{" "}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-8"
            onClick={() => setRetryTick((tick) => tick + 1)}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (loading && !plan) {
    body = (
      <div className="py-4 text-center" role="status">
        <Loader2 className="inline size-5 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading the latest sync plan…</span>
      </div>
    );
  } else if (!plan) {
    body = (
      <p className="m-0 text-sm">
        No sync plan yet. Once a target is approved, your executor records a snapshot of the account and asks Mono
        for the plan — the orders that move the account to the target. They show here, with their fills.
      </p>
    );
  } else {
    const orders = Array.isArray(plan.orders) ? plan.orders : [];
    const skipped = Array.isArray(plan.skipped) ? plan.skipped : [];
    // fills/?plan= lists only fills linked to this plan's orders.
    const byOrder = new Map();
    for (const fill of fills) {
      if (!byOrder.has(fill.order)) byOrder.set(fill.order, []);
      byOrder.get(fill.order).push(fill);
    }
    body = (
      <>
        <p className="m-0 text-sm font-medium">{planStatusLabel(plan.status)}</p>
        <p className="m-0 text-sm text-muted-foreground wrap-anywhere">
          To target v{plan.target_version}, from snapshot #{plan.snapshot} · made by {whoName(plan.created_by)} on{" "}
          {formatInstant(plan.created_at)}
          {LIVE.has(plan.status) ? ` · expires ${formatInstant(plan.expires_at)} if not finished` : ""}
        </p>
        <p className="m-0 text-xs text-muted-foreground">
          Rules it used: a drift band of {plan.drift_band_pct} pp, a minimum trade of {formatUsd(plan.min_trade_usd)},
          limits {formatPct(plan.limit_slippage_pct)} from the price.
          {plan.buy_scale && Number(plan.buy_scale) !== 1
            ? ` Buys were scaled by ×${plan.buy_scale} to fit the cash.`
            : ""}
        </p>
        {total > 1 && <p className="m-0 text-xs text-muted-foreground">The newest of {total} plans.</p>}
        {orders.length > 0 ? (
          <ul aria-label="Orders" className="mt-2 mb-0 flex list-none flex-col p-0">
            {orders.map((order) => (
              <OrderRow key={order.id} order={order} fills={byOrder.get(order.id) || []} />
            ))}
          </ul>
        ) : (
          <p className="mt-2 mb-0 text-sm">No orders: the account was already within the drift band of the target.</p>
        )}
        {fillsTotal > fills.length && (
          <p className="mt-1 mb-0 text-xs text-muted-foreground">
            Showing the newest {fills.length} of its {fillsTotal} fills.
          </p>
        )}
        {skipped.length > 0 && (
          <div className="mt-2">
            <h3 className="m-0 text-sm font-semibold">Skipped</h3>
            <ul aria-label="Skipped" className="m-0 flex list-none flex-col p-0">
              {skipped.map((skip) => (
                <li key={skip.symbol} className="text-sm">
                  {skip.symbol} — {skipReasonLabel(skip.reason)} (target {formatPct(skip.target_weight)}, held{" "}
                  {formatPct(skip.actual_weight)})
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }

  return (
    <section aria-label="Latest sync plan" className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">
        {plan ? `Latest sync plan — #${plan.id}` : "Latest sync plan"}
      </h2>
      {body}
    </section>
  );
}

SyncPlanPanel.propTypes = {
  /** Moves when the page reloads: the plan is read again. */
  reloadTick: PropTypes.number.isRequired,
};

export default SyncPlanPanel;
